import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { useEffect } from 'react'
import { clearIntendedRoute, setIntendedRoute } from '@/lib/intended-route'
import { useEnsureUserExists } from '@/services/convex-user.service'

type ProtectedRouteProps = {
  children: React.ReactNode
}

function LoadingState() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="space-y-4 text-center">
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>
  )
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isLoaded, isSignedIn } = useAuth()
  const location = useLocation()
  const intendedPath = `${location.pathname}${location.search}${location.hash}`
  const ensureUserExists = useEnsureUserExists()

  // Ensure user exists in Convex when they access a protected route
  // This handles the race condition between Clerk signup and webhook
  useEffect(() => {
    if (isSignedIn) {
      ensureUserExists()
        .catch((error) => {
          // Log but don't block - queries will handle missing user gracefully
          console.error('Failed to ensure user exists:', error)
        })
    }
  }, [isSignedIn, ensureUserExists])

  if (!isLoaded) {
    return <LoadingState />
  }

  if (!isSignedIn) {
    setIntendedRoute(intendedPath)
    return <Navigate to="/auth" replace state={{ from: intendedPath }} />
  }

  // Note: Clerk handles email verification automatically
  // If you require additional verification checks, you can add them here
  // using the useUser() hook to check user.emailAddresses[0].verification

  clearIntendedRoute()
  return <>{children}</>
}

